/**
 * HC Cart Utilities - Shared cart functionality
 * Used by both hc-cart.js and hc-cart-drawer.js
 */

window.HCCartUtils = {
  // Global cart state management
  state: {
    isUpdating: false,
    pendingUpdates: new Map(),
    retryAttempts: new Map(),
    maxRetries: 3
  },

  // Initialize cart utilities
  init() {
    if (window.hcCartUtilsInitialized) return;
    window.hcCartUtilsInitialized = true;
    console.log('HC Cart Utils initialized');
  },

  // Update quantity with proper error handling
  async updateQuantity(line, quantity, name, variantId, options = {}) {
    const updateKey = `${line}-${quantity}`;
    
    // Prevent duplicate updates
    if (this.state.pendingUpdates.has(updateKey)) {
      console.log('Update already pending, ignoring');
      return;
    }
    
    // Set global updating state
    this.state.isUpdating = true;
    this.state.pendingUpdates.set(updateKey, { line, quantity, name, variantId });
    
    console.log('Updating quantity:', { line, quantity, name, variantId });
    
    const body = JSON.stringify({
      line,
      quantity,
      sections: options.sections || this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname
    });

    const errors = document.getElementById('cart-errors') || 
                  document.getElementById('CartDrawer-CartErrors');

    try {
      const response = await fetch(`${routes.cart_change_url}`, {...fetchConfig(), ...{ body }});
      const text = await response.text();
      
      // Always try to parse the response, even if status is not ok
      const parsedState = JSON.parse(text);
      
      // Handle API errors (including 422 availability errors)
      if (parsedState.errors) {
        console.log('API returned errors:', parsedState.errors);
        
        // Show the actual error message from the server
        const errorMessage = Array.isArray(parsedState.errors) 
          ? parsedState.errors.join(' ') 
          : parsedState.errors;
        this.showError(errors, errorMessage);
        
        // Revert the input value to previous state
        const quantityElement = document.getElementById(`Quantity-${line}`) || 
                               document.getElementById(`Drawer-quantity-${line}`);
        if (quantityElement) {
          quantityElement.value = quantityElement.getAttribute('value');
        }
        return;
      }

      // Handle empty cart
      if (parsedState.item_count === 0) {
        console.log('Cart is empty, refreshing page');
        if (options.onEmpty) {
          options.onEmpty();
        } else {
          window.location.reload();
        }
        return;
      }

      // Success - refresh page to show updated state
      console.log('Cart updated successfully, refreshing page');
      if (options.onSuccess) {
        options.onSuccess(parsedState);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Cart update error:', error);
      
      // Show network error message - only for actual network issues
      this.showError(errors, 'Network error. Please check your connection and try again.');
      
      // Revert the input value to previous state
      const quantityElement = document.getElementById(`Quantity-${line}`) || 
                             document.getElementById(`Drawer-quantity-${line}`);
      if (quantityElement) {
        quantityElement.value = quantityElement.getAttribute('value');
      }
    } finally {
      // Clean up state
      this.state.isUpdating = false;
      this.state.pendingUpdates.delete(updateKey);
    }
  },

  // Get sections to render for cart updates
  getSectionsToRender() {
    return [
      {
        section: 'main-cart-items',
        id: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.js-contents',
      },
      {
        section: 'cart-icon-bubble',
        id: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        section: 'cart-live-region-text',
        id: 'cart-live-region-text',
        selector: '.shopify-section'
      },
      {
        section: 'main-cart-footer',
        id: document.getElementById('main-cart-items')?.dataset.id,
        selector: '.cart__footer__js-contents',
      },
      {
        section: 'hc-cart-drawer',
        id: 'shopify-section-hc-cart-drawer',
        selector: '.hc-cart-drawer__items'
      }
    ].filter(section => section.id);
  },

  // Show error message
  showError(errorsElement, customMessage = null) {
    if (errorsElement) {
      errorsElement.classList.remove('hidden');
      const errorText = errorsElement.querySelector('span');
      if (errorText) {
        errorText.textContent = customMessage || window.cartStrings?.error || 'An error occurred. Please try again.';
      }
      
      // Auto-hide error message after 5 seconds
      setTimeout(() => {
        if (errorsElement) {
          errorsElement.classList.add('hidden');
        }
      }, 5000);
    }
  },

  // Setup quantity input with shared logic
  setupQuantityInput(element, options = {}) {
    if (element.hcInitialized) return;
    element.hcInitialized = true;

    console.log('Setting up quantity input:', element);

    // Find the input and buttons
    const input = element.querySelector('input');
    const plusButton = element.querySelector('button[name="plus"]');
    const minusButton = element.querySelector('button[name="minus"]');

    if (!input || !plusButton || !minusButton) {
      console.log('Missing input or buttons:', { input: !!input, plusButton: !!plusButton, minusButton: !!minusButton });
      return;
    }

    // Handle button clicks directly
    const handleButtonClick = function(event, buttonName) {
      event.preventDefault();
      event.stopPropagation();
      
      // Get the actual button element (might be clicked on SVG or span inside)
      const button = event.target.closest('button') || event.currentTarget;
      
      console.log('HC Cart button click:', { 
        buttonName, 
        currentValue: input.value,
        button: button
      });
      
      // Prevent multiple clicks during cart updates
      if (window.HCCartUtils.state.isUpdating) {
        console.log('Cart is updating, ignoring button click');
        return;
      }
      
      // Prevent rapid clicking
      if (button.lastClickTime && Date.now() - button.lastClickTime < 300) {
        console.log('Click too rapid, ignoring');
        return;
      }
      button.lastClickTime = Date.now();
      
      const previousValue = parseInt(input.value) || 0;
      const min = parseInt(input.dataset.min) || 1;
      const step = parseInt(input.step) || 1;
      let newValue = previousValue;
      
      // Calculate new value
      if (buttonName === 'plus') {
        if (previousValue === 0 && min > step) {
          newValue = min;
          input.value = newValue;
        } else {
          newValue = previousValue + step;
        }
      } else if (buttonName === 'minus') {
        // For cart items, allow going to 0 to remove the item
        newValue = Math.max(0, previousValue - step);
      } else {
        console.log('Unknown button name:', buttonName);
        return;
      }
      
      // Only proceed if value actually changed
      if (previousValue !== newValue) {
        console.log('Quantity changing:', { previousValue, newValue });
        
        // Update the input value
        input.value = newValue;
        
        // Call the update function with custom options
        if (options.onUpdate) {
          options.onUpdate(input.dataset.index, newValue, 'quantity', input.dataset.quantityVariantId);
        } else {
          // Default behavior - find cart items container
          const cartItems = element.closest('hc-cart-items') || element.closest('cart-items');
          if (cartItems && cartItems.updateQuantity) {
            console.log('Calling cart updateQuantity');
            cartItems.updateQuantity(
              input.dataset.index,
              newValue,
              'quantity',
              input.dataset.quantityVariantId
            );
          } else {
            console.log('No cart items container found');
          }
        }
      } else {
        console.log('No quantity change needed');
      }
    };

    // Add click listeners to buttons with specific handlers
    plusButton.addEventListener('click', function(event) {
      handleButtonClick(event, 'plus');
    });
    minusButton.addEventListener('click', function(event) {
      handleButtonClick(event, 'minus');
    });

    // Handle input changes
    const handleInputChange = this.debounce(function(event) {
      const { index, value, variantId } = event.target.dataset;
      
      if (options.onUpdate) {
        options.onUpdate(index, value, 'quantity', variantId);
      } else {
        const cartItems = element.closest('hc-cart-items') || element.closest('cart-items');
        if (cartItems && cartItems.updateQuantity) {
          cartItems.updateQuantity(index, value, 'quantity', variantId);
        }
      }
    }, 500);

    input.addEventListener('change', handleInputChange);
  },

  // Setup remove button with shared logic
  setupRemoveButton(element, options = {}) {
    if (element.hcInitialized) return;
    element.hcInitialized = true;

    // Remove any existing event listeners to prevent duplicates
    element.removeEventListener('click', element.hcClickHandler);
    
    // Create a bound handler
    element.hcClickHandler = function(event) {
      event.preventDefault();
      event.stopPropagation();
      
      // Prevent multiple clicks during updates
      if (window.HCCartUtils.state.isUpdating) {
        console.log('Cart is updating, ignoring click');
        return;
      }
      
      if (options.onRemove) {
        options.onRemove(element.dataset.index);
      } else {
        // Default behavior
        const cartItems = element.closest('hc-cart-items') || element.closest('cart-items');
        if (cartItems && cartItems.updateQuantity) {
          cartItems.updateQuantity(element.dataset.index, 0);
        } else {
          window.location.reload();
        }
      }
    };
    
    element.addEventListener('click', element.hcClickHandler);
  },

  // Utility function for debouncing
  debounce(func, wait, immediate = false) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        timeout = null;
        if (!immediate) func(...args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func(...args);
    };
  }
};

// Initialize utilities when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  window.HCCartUtils.init();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', window.HCCartUtils.init);
} else {
  window.HCCartUtils.init();
}
